/**
 * Phase 138B — StreamX Subscription State Banner
 *
 * Proactive dunning surface for academy owners. Renders ONE of:
 *   • `suspended`  → red banner, "Complete Payment" inline Razorpay CTA (streams blocked)
 *   • `past_due`   → amber banner, "Pay Now" inline Razorpay CTA (streams still allowed)
 *   • `renewing_soon` → gold banner, "Renew Now" inline Razorpay CTA (Pro sub expires ≤3d)
 *   • null         → renders nothing (active + healthy, or pilot)
 *
 * Phase 138-R — One-click inline Razorpay checkout. Replaces the `<Link to=
 * "/streamx/subscription">` navigation with `<LivexCheckoutHandler />` so the
 * suspended/past_due/renewing academy can recover in a Razorpay overlay WITHOUT
 * leaving the dashboard tab. On verify-payment success the page is reloaded so
 * the banner disappears + workspace features unlock immediately.
 */
import { Link } from 'react-router-dom';
import { AlertTriangle, Lock, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import LivexCheckoutHandler from './LivexCheckoutHandler';

function daysUntil(iso) {
  if (!iso) return null;
  try {
    const ms = new Date(iso).getTime() - Date.now();
    return Math.ceil(ms / (24 * 3600 * 1000));
  } catch { return null; }
}

export default function LivexSubscriptionStateBanner({ subscription }) {
  const { user } = useAuth();
  const sub = subscription?.subscription;
  if (!sub) return null;

  const status = sub.status;
  const plan = sub.plan;
  // Phase 138-R — renew using the same billing cycle the academy was already on
  const billingCycle = sub.billing_cycle || 'monthly';

  // Reload after successful inline recovery so the banner disappears and any
  // suspended workspace features unlock without a manual nav action.
  const handleRecoverySuccess = (result) => {
    toast.success(
      result?.mocked
        ? '✅ Subscription reactivated (TEST mode)'
        : '🎉 Payment received · Subscription restored'
    );
    // Tiny delay so the toast paints before reload
    window.setTimeout(() => { window.location.reload(); }, 600);
  };
  const handleRecoveryError = (msg) => {
    toast.error(msg || 'Payment failed — please try again');
  };

  // SUSPENDED — hard block. Red, urgent, immutable.
  if (status === 'suspended') {
    return (
      <div
        data-testid="livex-banner-suspended"
        className="rounded-2xl border-2 p-5 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4"
        style={{
          background: 'linear-gradient(135deg, #1a0507, #2a0a0c)',
          borderColor: '#dc2626',
          boxShadow: '0 0 32px rgba(220, 38, 38, 0.25)',
        }}
      >
        <div
          className="flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center"
          style={{ background: 'rgba(220, 38, 38, 0.18)', border: '1px solid rgba(220, 38, 38, 0.45)' }}
        >
          <Lock className="h-6 w-6 text-rose-300" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] tracking-[0.25em] font-black text-rose-300 mb-1">🔒 STREAMX SUSPENDED</div>
          <h3 className="text-lg sm:text-xl font-black text-white leading-tight mb-1">
            Streams paused — invoice overdue
          </h3>
          <p className="text-sm text-rose-100/80 leading-snug">
            New live streams are blocked until your StreamX invoice is paid. Your replay archive remains publicly viewable.
          </p>
        </div>
        <div className="flex flex-col items-stretch gap-1.5 shrink-0">
          <LivexCheckoutHandler
            billingCycle={billingCycle}
            user={user}
            onSuccess={handleRecoverySuccess}
            onError={handleRecoveryError}
            label="Complete Payment"
            variant="compact"
            testid="livex-banner-suspended-cta"
            className="!px-5 !py-2.5 !text-sm whitespace-nowrap"
          />
          <Link
            to="/streamx/subscription"
            className="text-[10px] text-rose-200/70 hover:text-rose-100 text-center underline-offset-2 hover:underline"
            data-testid="livex-banner-suspended-view-invoices"
          >
            View invoices →
          </Link>
        </div>
      </div>
    );
  }

  // PAST_DUE — yellow grace state. Streams still allowed but visible pressure.
  if (status === 'past_due') {
    return (
      <div
        data-testid="livex-banner-past-due"
        className="rounded-2xl border-2 p-5 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4"
        style={{
          background: 'linear-gradient(135deg, #1f1505, #2a1d08)',
          borderColor: '#f59e0b',
          boxShadow: '0 0 24px rgba(245, 158, 11, 0.18)',
        }}
      >
        <div
          className="flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center"
          style={{ background: 'rgba(245, 158, 11, 0.16)', border: '1px solid rgba(245, 158, 11, 0.45)' }}
        >
          <AlertTriangle className="h-6 w-6 text-amber-300" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[11px] tracking-[0.25em] font-black text-amber-300 mb-1">⚠ PAYMENT OVERDUE</div>
          <h3 className="text-lg sm:text-xl font-black text-white leading-tight mb-1">
            Pay invoice to keep streaming uninterrupted
          </h3>
          <p className="text-sm text-amber-100/80 leading-snug">
            Your StreamX invoice is past due. You can still stream during the grace period — pay within 2 days to avoid automatic suspension.
          </p>
        </div>
        <div className="flex flex-col items-stretch gap-1.5 shrink-0">
          <LivexCheckoutHandler
            billingCycle={billingCycle}
            user={user}
            onSuccess={handleRecoverySuccess}
            onError={handleRecoveryError}
            label="Pay Now"
            variant="compact"
            testid="livex-banner-past-due-cta"
            className="!px-5 !py-2.5 !text-sm whitespace-nowrap"
          />
          <Link
            to="/streamx/subscription"
            className="text-[10px] text-amber-200/70 hover:text-amber-100 text-center underline-offset-2 hover:underline"
            data-testid="livex-banner-past-due-view-invoices"
          >
            View invoices →
          </Link>
        </div>
      </div>
    );
  }

  // RENEWING_SOON — Pro sub expires within 3 days. Calm gold, opportunity-tone.
  if (plan === 'pro' && status === 'active' && sub.expires_at) {
    const days = daysUntil(sub.expires_at);
    if (days !== null && days >= 0 && days <= 3) {
      return (
        <div
          data-testid="livex-banner-renewing-soon"
          className="rounded-2xl border-2 p-5 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4"
          style={{
            background: 'linear-gradient(135deg, #1a1305, #2a1e08)',
            borderColor: '#FFB800',
            boxShadow: '0 0 24px rgba(255, 184, 0, 0.16)',
          }}
        >
          <div
            className="flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center"
            style={{ background: 'rgba(255, 184, 0, 0.14)', border: '1px solid rgba(255, 184, 0, 0.45)' }}
          >
            <Sparkles className="h-6 w-6" style={{ color: '#FFB800' }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] tracking-[0.25em] font-black mb-1" style={{ color: '#FFB800' }}>
              ⭐ RENEWAL DUE SOON
            </div>
            <h3 className="text-lg sm:text-xl font-black text-white leading-tight mb-1">
              {days === 0 ? 'Your StreamX Pro renewal is today' : `Your StreamX Pro renewal is in ${days} day${days === 1 ? '' : 's'}`}
            </h3>
            <p className="text-sm text-white/70 leading-snug">
              Renew now to keep your replay archive permanent, broadcast reliability uninterrupted, and avoid any service gap.
            </p>
          </div>
          <div className="flex flex-col items-stretch gap-1.5 shrink-0">
            <LivexCheckoutHandler
              billingCycle={billingCycle}
              user={user}
              onSuccess={handleRecoverySuccess}
              onError={handleRecoveryError}
              label="Renew Pro"
              variant="compact"
              testid="livex-banner-renewing-soon-cta"
              className="!px-5 !py-2.5 !text-sm whitespace-nowrap"
            />
            <Link
              to="/streamx/subscription"
              className="text-[10px] text-amber-200/70 hover:text-amber-100 text-center underline-offset-2 hover:underline"
              data-testid="livex-banner-renewing-soon-details"
            >
              See plan details →
            </Link>
          </div>
        </div>
      );
    }
  }

  return null;
}
